import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Search, 
  Globe, 
  ArrowLeft, 
  TrendingDown, 
  TrendingUp,
  Loader2,
  History,
  Flag,
  DollarSign,
  AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_FLAGS = {
  ch: "🇨🇭",
  fr: "🇫🇷",
  it: "🇮🇹",
  es: "🇪🇸"
};

const COUNTRY_COLORS = {
  ch: "bg-red-500",
  fr: "bg-blue-500",
  it: "bg-green-500",
  es: "bg-yellow-500"
};

export default function PriceSearch() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [itemDescription, setItemDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("pce");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadHistory();
  }, [token]);

  const loadHistory = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/search/multi-country/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.searches || []);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const handleSearch = async () => {
    if (!itemDescription.trim()) {
      toast.error("Veuillez entrer une description d'article");
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await fetch(`${API_URL}/api/search/multi-country`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          item_description: itemDescription,
          quantity: parseFloat(quantity) || 1,
          unit,
          provider: "perplexity"  // Utilise Perplexity par défaut (meilleur pour recherche web)
        })
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la recherche");
      }

      const data = await response.json();
      setResults(data);
      loadHistory(); // Refresh history
      toast.success(`Recherche terminée - ${data.countries_found || 0} pays avec résultats`);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = "CHF") => {
    if (!price && price !== 0) return "N/A";
    return new Intl.NumberFormat("fr-CH", {
      style: "currency",
      currency: currency === "EUR" ? "EUR" : "CHF"
    }).format(price);
  };

  const CountryResult = ({ code, result, isBest, isWorst }) => {
    const flag = COUNTRY_FLAGS[code] || "🏳️";
    const colorClass = COUNTRY_COLORS[code] || "bg-gray-500";
    
    return (
      <Card className={`relative overflow-hidden ${isBest ? "ring-2 ring-green-500" : ""} ${isWorst ? "ring-2 ring-red-300" : ""}`}>
        {isBest && (
          <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-bl-lg font-medium">
            Meilleur prix
          </div>
        )}
        {isWorst && !isBest && (
          <div className="absolute top-0 right-0 bg-red-400 text-white text-xs px-2 py-1 rounded-bl-lg font-medium">
            Le plus cher
          </div>
        )}
        
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{flag}</span>
            <div>
              <CardTitle className="text-lg">{result.country || result.country_name}</CardTitle>
              <Badge variant="outline" className="text-xs">{result.currency}</Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {result.found ? (
            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-[#1E8B5A]">
                  {formatPrice(result.price_estimate || result.unit_price, result.currency)}
                </span>
                <span className="text-gray-500">/{unit}</span>
              </div>
              
              {(result.price_estimate_chf || result.unit_price_chf) && result.currency !== "CHF" && (
                <div className="text-sm text-gray-600">
                  ≈ {formatPrice(result.price_estimate_chf || result.unit_price_chf)} CHF
                </div>
              )}
              
              {result.price_min && result.price_max && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <TrendingDown className="h-4 w-4 text-green-500" />
                  {formatPrice(result.price_min, result.currency)}
                  <span>-</span>
                  <TrendingUp className="h-4 w-4 text-red-400" />
                  {formatPrice(result.price_max, result.currency)}
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Badge 
                  variant={result.confidence === "high" || result.confidence > 0.7 ? "default" : "secondary"}
                  className={result.confidence === "high" || result.confidence > 0.7 ? "bg-green-500" : ""}
                >
                  {typeof result.confidence === "number" 
                    ? `${Math.round(result.confidence * 100)}%` 
                    : result.confidence}
                </Badge>
                <span className="text-xs text-gray-500">confiance</span>
              </div>
              
              {result.source && (
                <p className="text-xs text-gray-500 line-clamp-2">{result.source}</p>
              )}
              
              {result.notes && (
                <p className="text-xs text-gray-500 line-clamp-2 mt-2 italic">{result.notes}</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-400 py-4">
              <AlertCircle className="h-5 w-5" />
              <span>Aucun prix trouvé</span>
              {result.error && (
                <span className="text-xs text-red-400">({result.error})</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Globe className="h-6 w-6 text-[#1E8B5A]" />
                <h1 className="text-xl font-semibold">Recherche de Prix Multi-Pays</h1>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              data-testid="toggle-history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              Historique
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Rechercher un prix
            </CardTitle>
            <CardDescription>
              Comparez les prix dans 4 pays européens: 🇨🇭 Suisse, 🇫🇷 France, 🇮🇹 Italie, 🇪🇸 Espagne
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <Label htmlFor="item">Description de l'article</Label>
                <Input
                  id="item"
                  placeholder="ex: Thuya Brabant 150/175, Béton C25/30, Gravier 8/16..."
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="item-description-input"
                />
              </div>
              
              <div>
                <Label htmlFor="quantity">Quantité</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  data-testid="quantity-input"
                />
              </div>
              
              <div>
                <Label htmlFor="unit">Unité</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger id="unit" data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pce">pce (pièce)</SelectItem>
                    <SelectItem value="m²">m²</SelectItem>
                    <SelectItem value="m³">m³</SelectItem>
                    <SelectItem value="ml">ml (mètre linéaire)</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="t">t (tonne)</SelectItem>
                    <SelectItem value="h">h (heure)</SelectItem>
                    <SelectItem value="forfait">forfait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button 
                onClick={handleSearch} 
                disabled={loading || !itemDescription.trim()}
                className="bg-[#1E8B5A] hover:bg-[#166B45]"
                data-testid="search-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Rechercher dans 4 pays
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <div className="space-y-6">
            {/* Summary */}
            {results.countries_found > 0 && (
              <Card className="bg-gradient-to-r from-[#1E8B5A]/10 to-[#166B45]/5">
                <CardContent className="py-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-sm text-gray-500">Meilleur prix</div>
                      <div className="text-xl font-bold text-green-600">
                        {results.best_price?.country} {COUNTRY_FLAGS[results.best_price?.country_code]}
                      </div>
                      <div className="text-lg font-semibold">
                        {formatPrice(results.best_price?.price_chf)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-gray-500">Prix moyen</div>
                      <div className="text-xl font-bold text-gray-700">
                        {formatPrice(results.average_price_chf)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-gray-500">Écart de prix</div>
                      <div className="text-xl font-bold text-amber-600">
                        {results.price_spread_percent ? `${results.price_spread_percent}%` : "N/A"}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-gray-500">Pays avec résultats</div>
                      <div className="text-xl font-bold text-blue-600">
                        {results.countries_found}/4
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-gray-500">Taux EUR/CHF</div>
                      <div className="text-xl font-bold text-gray-600">
                        {results.exchange_rate_eur_chf ? results.exchange_rate_eur_chf.toFixed(4) : "0.94"}
                      </div>
                      <div className="text-xs text-gray-400">BCE - quotidien</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Country Results Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {results.results_by_country && Object.entries(results.results_by_country).map(([code, result]) => (
                <CountryResult
                  key={code}
                  code={code}
                  result={result}
                  isBest={results.best_price?.country_code === code}
                  isWorst={results.worst_price?.country_code === code}
                />
              ))}
            </div>

            {/* Price Ranking */}
            {results.prices_ranking && results.prices_ranking.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Classement des prix (CHF)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.prices_ranking.map((item, index) => (
                      <div 
                        key={item.country_code}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          index === 0 ? "bg-green-50 border border-green-200" : 
                          index === results.prices_ranking.length - 1 ? "bg-red-50 border border-red-200" :
                          "bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-bold text-gray-300">#{index + 1}</span>
                          <span className="text-xl">{COUNTRY_FLAGS[item.country_code]}</span>
                          <span className="font-medium">{item.country}</span>
                        </div>
                        <span className="text-lg font-bold">
                          {formatPrice(item.price_chf)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* History Sidebar */}
        {showHistory && (
          <div className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-80 bg-white border-l shadow-lg overflow-y-auto p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <History className="h-4 w-4" />
              Historique des recherches
            </h3>
            
            {history.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucune recherche récente</p>
            ) : (
              <div className="space-y-3">
                {history.map((search) => (
                  <Card 
                    key={search.id} 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      setItemDescription(search.item_description);
                      setQuantity(String(search.quantity || 1));
                      setUnit(search.unit || "pce");
                      setResults(search.results);
                      setShowHistory(false);
                    }}
                  >
                    <CardContent className="p-3">
                      <p className="font-medium text-sm line-clamp-1">{search.item_description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{search.quantity} {search.unit}</span>
                        {search.results?.countries_found && (
                          <>
                            <span>•</span>
                            <span>{search.results.countries_found}/4 pays</span>
                          </>
                        )}
                      </div>
                      {search.results?.best_price && (
                        <div className="mt-2 text-xs">
                          <Badge variant="outline" className="text-green-600">
                            {COUNTRY_FLAGS[search.results.best_price.country_code]} {formatPrice(search.results.best_price.price_chf)}
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
